import type { IRecordTypeDefinition, IRecordTypeOverride } from '@platform/core/types';
import { ApiError } from './ApiError';
import type { HttpClient } from './HttpClient';
import type { IGlobalModuleWrite } from './IGlobalModuleWrite';
import type { IPlatformCatalog } from './IPlatformCatalog';
import type { IRecordTypeOverrideDoc } from './IRecordTypeOverrideDoc';
import { platformCatalogOf } from './platformCatalogOf';
import { toQuery } from './toQuery';

const overridePath = (rt: string) => `/platform/record-types/${encodeURIComponent(rt)}/override`;
const modulePath = (key: string) => `/platform/modules/${encodeURIComponent(key)}`;

/**
 * Глобальні визначення (ADR-027): шар між опублікованим визначенням і перевизначенням простору (опубліковане → глобальне → простір).
 * Глобальний каталог, глобальні назва й доступність модуля, глобальне перевизначення типу запису (той самий патч, що ADR-023: читання —
 * 404 означає «ще немає», збереження з If-Match версії, скидання). Змінюють адміністратор платформи й налаштувальник.
 */
export class GlobalDefinitionsDao {
  constructor(private readonly http: HttpClient) {}

  async catalog(): Promise<IPlatformCatalog> { return platformCatalogOf(await this.http.get<unknown>('/platform/catalog')); }
  async updateModule(key: string, body: IGlobalModuleWrite): Promise<void> { await this.http.patch<unknown>(modulePath(key), body); }
  /** Скинути глобальне: без типу — назва й доступність модуля та перевизначення всіх його типів; з типом — перевизначення цього типу (головного — ще й назва й доступність). */
  async resetModule(key: string, recordType?: string): Promise<void> { await this.http.post<unknown>(`${modulePath(key)}/reset${toQuery({ recordType })}`); }

  async override(rt: string): Promise<IRecordTypeOverrideDoc> {
    try {
      return await this.http.get<IRecordTypeOverrideDoc>(overridePath(rt));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return { patch: {}, version: 0 };
      throw e;
    }
  }
  saveOverride(rt: string, patch: IRecordTypeOverride, version: number): Promise<IRecordTypeDefinition> { return this.http.put(overridePath(rt), { patch }, version); }
  resetOverride(rt: string): Promise<void> { return this.http.delete(overridePath(rt)); }
}
